import { useEffect } from 'react';
import type { Surah } from '@/types/domain';

/**
 * Per-route SEO injector for /surah/:id pages.
 *
 * The app is a Vite SPA, so `index.html` ships one static set of meta
 * tags. Without this component every surah inherits the homepage
 * description and canonicals back to "/", which means Googlebot can't
 * distinguish 114 nearly-identical pages and they cannibalize each
 * other for ranking.
 *
 * On mount (and whenever the surah changes) this component upserts:
 *   - <title>                  — keyword-rich, per-surah
 *   - <meta name="description"> — per-surah summary
 *   - <link rel="canonical">    — slugged URL on alhudah.com
 *   - Open Graph + Twitter card meta
 *   - JSON-LD Article schema (with isPartOf -> Quran Book schema)
 *
 * On unmount the canonical resets to the homepage so other routes
 * aren't left pointing at the wrong page. The other tags get
 * overwritten by the next route's <SurahSeo> or stay as-is for
 * routes that don't ship their own injector — both behaviors are
 * acceptable; Google re-reads on every navigation.
 *
 * No external deps (e.g. react-helmet) — manual DOM upsert keeps
 * the bundle lean.
 */
export function SurahSeo({ surah }: { surah: Surah }) {
  useEffect(() => {
    // Numeric canonical (Option A) — matches the actual route the
    // SPA serves today (/surah/12). Switching to a slugged canonical
    // would require a slug-aware route handler first; until then,
    // keep canonical aligned with what Googlebot can actually fetch.
    const url = `https://alhudah.com/surah/${surah.id}`;

    const title =
      `Surah ${surah.name_en} — Arabic, English & Urdu Translation · Alhudah`;

    const meaningClause = surah.name_translation
      ? ` (${surah.name_translation})`
      : '';
    const revelationClause =
      surah.revelation_type === 'meccan' ? 'revealed in Mecca' : 'revealed in Medina';

    const description =
      `Read Surah ${surah.name_en}${meaningClause} with Arabic text, ` +
      `English translation by Sahih International, Urdu by Jalandhari, ` +
      `and audio recitation by Mishary Alafasy. ${surah.ayah_count} verses, ${revelationClause}.`;

    document.title = title;
    upsertMeta('description', description);
    upsertLink('canonical', url);

    // Open Graph
    upsertMeta('og:title', `Surah ${surah.name_en} — Quran with Translation`, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:url', url, 'property');
    upsertMeta('og:type', 'article', 'property');

    // Twitter
    upsertMeta('twitter:title', `Surah ${surah.name_en} — Quran with Translation`);
    upsertMeta('twitter:description', description);

    // JSON-LD: Article inside the Quran Book.
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline:
        `Surah ${surah.name_en}` +
        (surah.name_translation ? ` (${surah.name_translation})` : '') +
        ' — Quran Translation',
      inLanguage: ['ar', 'en', 'ur'],
      isPartOf: {
        '@type': 'Book',
        name: 'The Holy Quran',
        alternateName: 'القرآن الكريم',
        inLanguage: 'ar',
      },
      about: {
        '@type': 'Thing',
        name: `Surah ${surah.name_en}`,
        alternateName: surah.name_ar,
      },
      url,
      mainEntityOfPage: url,
      publisher: {
        '@type': 'Organization',
        name: 'Alhudah',
        url: 'https://alhudah.com',
        logo: {
          '@type': 'ImageObject',
          url: 'https://alhudah.com/favicon.svg',
        },
      },
    };
    upsertJsonLd('surah-jsonld', jsonLd);

    return () => {
      // Reset canonical to site root on unmount so the next route
      // doesn't inherit a surah-specific URL.
      upsertLink('canonical', 'https://alhudah.com/');
    };
  }, [surah]);

  return null;
}

// ----- DOM helpers ---------------------------------------------------

function upsertMeta(
  name: string,
  content: string,
  attr: 'name' | 'property' = 'name',
): void {
  const selector = `meta[${attr}="${name}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id: string, payload: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}
